import { DisplayObject, Point } from 'pixi.js';

export type WallDirection = 'horizontal' | 'vertical';

export interface WallSegment {
  id: string;
  type: 'wall';
  floor: number;
  direction: WallDirection;
  start: Point;
  end: Point;
  graphic?: DisplayObject;
}

interface NormalizedEdge {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  direction: WallDirection;
}

export class WallSystem {
  private readonly segments: Map<string, WallSegment> = new Map();

  public clear(): void {
    this.segments.clear();
  }

  public getSegment(
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    floor = 0
  ): WallSegment | undefined {
    const key = this.getKey(startX, startY, endX, endY, floor);
    return this.segments.get(key);
  }

  public isOccupied(
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    floor = 0
  ): boolean {
    return Boolean(this.getSegment(startX, startY, endX, endY, floor));
  }

  public canPlace(
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    floor = 0
  ): boolean {
    if (!this.isAdjacent(startX, startY, endX, endY)) {
      return false;
    }

    return !this.isOccupied(startX, startY, endX, endY, floor);
  }

  public addSegment(
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    floor = 0
  ): WallSegment {
    const normalized = this.normalizeEdge(startX, startY, endX, endY);
    const key = this.getKey(normalized.startX, normalized.startY, normalized.endX, normalized.endY, floor);

    if (this.segments.has(key)) {
      const existing = this.segments.get(key);
      if (!existing) {
        throw new Error('Unexpected wall segment state');
      }
      return existing;
    }

    const segment: WallSegment = {
      id: `wall-${floor}-${normalized.startX}-${normalized.startY}-${normalized.endX}-${normalized.endY}`,
      type: 'wall',
      floor,
      direction: normalized.direction,
      start: new Point(normalized.startX, normalized.startY),
      end: new Point(normalized.endX, normalized.endY)
    };

    this.segments.set(key, segment);
    return segment;
  }

  public addSingleLineSegment(
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    floor = 0
  ): WallSegment {
    // Bypass adjacency check for single line walls
    const normalized = this.normalizeSingleLineEdge(startX, startY, endX, endY);
    const key = this.getSingleLineKey(normalized.startX, normalized.startY, normalized.endX, normalized.endY, floor);

    if (this.segments.has(key)) {
      const existing = this.segments.get(key);
      if (!existing) {
        throw new Error('Unexpected wall segment state');
      }
      return existing;
    }

    const segment: WallSegment = {
      id: `singlewall-${floor}-${normalized.startX}-${normalized.startY}-${normalized.endX}-${normalized.endY}`,
      type: 'wall',
      floor,
      direction: normalized.direction,
      start: new Point(normalized.startX, normalized.startY),
      end: new Point(normalized.endX, normalized.endY)
    };

    this.segments.set(key, segment);
    return segment;
  }

  public removeSegment(
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    floor = 0
  ): WallSegment | undefined {
    const key = this.getKey(startX, startY, endX, endY, floor);
    const existing = this.segments.get(key);
    this.segments.delete(key);
    return existing;
  }

  public getAllSegments(): WallSegment[] {
    return Array.from(this.segments.values());
  }

  private getKey(
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    floor: number
  ): string {
    const normalized = this.normalizeEdge(startX, startY, endX, endY);
    return `${floor}:${normalized.startX}:${normalized.startY}:${normalized.endX}:${normalized.endY}`;
  }

  private normalizeEdge(
    startX: number,
    startY: number,
    endX: number,
    endY: number
  ): NormalizedEdge {
    if (!this.isAdjacent(startX, startY, endX, endY)) {
      throw new Error(
        `Invalid wall edge coordinates (${startX}, ${startY}) -> (${endX}, ${endY})`
      );
    }

    let normalizedStartX = startX;
    let normalizedStartY = startY;
    let normalizedEndX = endX;
    let normalizedEndY = endY;

    // Ensure consistent ordering for keys (lexicographical by x then y)
    if (endX < startX || (endX === startX && endY < startY)) {
      normalizedStartX = endX;
      normalizedStartY = endY;
      normalizedEndX = startX;
      normalizedEndY = startY;
    }

    const direction: WallDirection = normalizedStartX === normalizedEndX ? 'horizontal' : 'vertical';

    return {
      startX: normalizedStartX,
      startY: normalizedStartY,
      endX: normalizedEndX,
      endY: normalizedEndY,
      direction
    };
  }

  private isAdjacent(startX: number, startY: number, endX: number, endY: number): boolean {
    const dx = Math.abs(endX - startX);
    const dy = Math.abs(endY - startY);
    return (dx === 1 && dy === 0) || (dx === 0 && dy === 1);
  }

  private normalizeSingleLineEdge(
    startX: number,
    startY: number,
    endX: number,
    endY: number
  ): NormalizedEdge {
    // For single line walls, skip adjacency check and allow any distance
    let normalizedStartX = startX;
    let normalizedStartY = startY;
    let normalizedEndX = endX;
    let normalizedEndY = endY;

    // Ensure consistent ordering for keys (lexicographical by x then y)
    if (endX < startX || (endX === startX && endY < startY)) {
      normalizedStartX = endX;
      normalizedStartY = endY;
      normalizedEndX = startX;
      normalizedEndY = startY;
    }

    // Determine direction based on the line orientation
    const direction: WallDirection = normalizedStartX === normalizedEndX ? 'vertical' : 'horizontal';

    return {
      startX: normalizedStartX,
      startY: normalizedStartY,
      endX: normalizedEndX,
      endY: normalizedEndY,
      direction
    };
  }

  private getSingleLineKey(
    startX: number,
    startY: number,
    endX: number,
    endY: number,
    floor: number
  ): string {
    return `singleline-${floor}:${startX}:${startY}:${endX}:${endY}`;
  }
}
